import { useState, useRef, useCallback, useEffect } from 'react'
import {
  getProgressForEpisode,
  saveProgressForEpisode,
} from './localStorageProgress'

export const useAudioPlayer = (episodeId?: string) => {
  const audioRef = useRef<any>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isSeeking, setIsSeeking] = useState(false)
  const [progressBarEl, setProgressBarEl] = useState<HTMLElement | null>(null)
  const [scrubTime, setScrubTime] = useState<number | null>(null)

  const hasResumedRef = useRef(false)
  const lastSavedTime = useRef<number>(0)

  const onPlay = () => {
    setIsPlaying(true)
  }

  const onPause = () => {
    setIsPlaying(false)
    if (episodeId && currentTime > 0) {
      saveProgressForEpisode(episodeId, Math.floor(currentTime))
    }
  }

  const onEnded = () => {
    setIsPlaying(false)
    setCurrentTime(0)
    if (episodeId) {
      saveProgressForEpisode(episodeId, Math.floor(currentTime))
    }
  }

  const onLoadedMetadata = (e: any) => {
    const d = e.target?.duration ?? audioRef.current?.audioEl?.current?.duration
    if (d) setDuration(d)

    if (episodeId && !hasResumedRef.current) {
      const saved = getProgressForEpisode(episodeId)
      if (saved && d && saved < d) {
        audioRef.current.audioEl.current.currentTime = saved
        setCurrentTime(saved)
      }
      hasResumedRef.current = true
    }
  }

  const onListen = (e: any) => {
    const time =
      e?.target?.currentTime ?? audioRef.current?.audioEl?.current?.currentTime
    if (time != null) {
      setCurrentTime(time)

      const rounded = Math.floor(time)
      if (episodeId && rounded - lastSavedTime.current >= 5) {
        lastSavedTime.current = rounded
        saveProgressForEpisode(episodeId, rounded)
      }
    }
  }

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current?.audioEl?.current
    if (!audio) return
    isPlaying ? audio.pause() : audio.play()
  }, [isPlaying])

  const play = useCallback(() => {
    const audio = audioRef.current?.audioEl?.current
    if (!audio) return
    if (!isPlaying) audio.play()
  }, [isPlaying])

  const seekTo = (time: number) => {
    if (!audioRef.current) return
    audioRef.current.audioEl.current.currentTime = time
    setCurrentTime(time)
  }

  const handleSeekBarClick = (e: React.MouseEvent<HTMLElement>) => {
    const bar = e.currentTarget
    const rect = bar.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const width = rect.width
    const percent = Math.min(Math.max(clickX / width, 0), 1)
    const newTime = percent * duration
    seekTo(newTime)
  }

  const handleSeekStart = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
  ) => {
    setIsSeeking(true)
    setProgressBarEl(e.currentTarget as HTMLElement)
    handleSeekPreview(e.nativeEvent)
  }

  const handleSeekPreview = (e: MouseEvent | React.MouseEvent | TouchEvent) => {
    if (!progressBarEl || duration === 0) return

    let clientX
    if ('touches' in e) {
      clientX = e.touches[0].clientX
    } else {
      clientX = e.clientX
    }

    const rect = progressBarEl.getBoundingClientRect()
    const moveX = clientX - rect.left
    const width = rect.width
    const percent = Math.min(Math.max(moveX / width, 0), 1)
    const previewTime = percent * duration
    setScrubTime(previewTime)
  }

  const handleSeekEnd = () => {
    if (scrubTime !== 0 && scrubTime !== null) {
      seekTo(scrubTime)
    }
    setScrubTime(null)
    setIsSeeking(false)
    setProgressBarEl(null)
  }

  useEffect(() => {
    if (isSeeking) {
      window.addEventListener('mousemove', handleSeekPreview)
      window.addEventListener('mouseup', handleSeekEnd)
      window.addEventListener('touchmove', handleSeekPreview)
      window.addEventListener('touchend', handleSeekEnd)
      document.body.style.userSelect = 'none'
    } else {
      window.removeEventListener('mousemove', handleSeekPreview)
      window.removeEventListener('mouseup', handleSeekEnd)
      window.removeEventListener('touchmove', handleSeekPreview)
      window.removeEventListener('touchend', handleSeekEnd)
      document.body.style.userSelect = ''
    }

    return () => {
      window.removeEventListener('mousemove', handleSeekPreview)
      window.removeEventListener('mouseup', handleSeekEnd)
      window.removeEventListener('touchmove', handleSeekPreview)
      window.removeEventListener('touchend', handleSeekEnd)
      document.body.style.userSelect = ''
    }
  }, [isSeeking, progressBarEl, scrubTime])

  const forward = () => {
    if (!audioRef.current) return
    audioRef.current.audioEl.current.currentTime += 10
  }

  const backward = () => {
    if (!audioRef.current) return
    audioRef.current.audioEl.current.currentTime -= 10
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const reset = () => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setIsSeeking(false)
    setScrubTime(null)
    setProgressBarEl(null)
    hasResumedRef.current = false
    lastSavedTime.current = 0
  }

  const visibleTime = isSeeking && scrubTime != null ? scrubTime : currentTime
  const progress = duration > 0 ? visibleTime / duration : 0

  return {
    audioRef,
    isPlaying,
    currentTime: visibleTime,
    duration,
    isSeeking,
    togglePlayPause,
    play,
    seekTo,
    forward,
    backward,
    formatTime,
    progress,
    handleSeekBarClick,
    handleSeekStart,
    reset,
    reactAudioPlayerProps: {
      onPlay,
      onPause,
      onEnded,
      onLoadedMetadata,
      onListen,
      listenInterval: 500,
    },
  }
}
